import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminStore } from '@/store/adminStore';
import { useAuthStore } from '@/store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Building2,
  Users,
  CreditCard,
  MessageCircle,
  Search,
  Ban,
  CheckCircle,
  Settings2,
  Wifi,
  WifiOff,
  Crown,
  Trash2,
  Package,
  ChevronUp,
  ChevronDown,
  FileText,
  Eye,
  ArrowUpDown,
  Filter,
  X,
  LogIn,
} from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/components/I18nProvider';

const SubscriptionBadge = ({ status }) => {
  const map = {
    trial: { label: 'Deneme', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    active: { label: 'Aktif', cls: 'bg-green-100 text-green-700 border-green-200' },
    expired: { label: 'Suresi Dolmus', cls: 'bg-red-100 text-red-700 border-red-200' },
    pending_payment: { label: 'Havale Onayı Bekliyor', cls: 'bg-orange-100 text-orange-700 border-orange-200' },
  };
  const s = map[status] || map.trial;
  return <Badge className={`${s.cls} text-[10px]`} data-testid={`sub-badge-${status}`}>{s.label}</Badge>;
};

const SummaryCard = ({ title, value, color }) => (
  <div className={`rounded-lg border p-3 text-center ${color}`}>
    <p className="text-xl sm:text-2xl font-bold">{value}</p>
    <p className="text-[11px] text-muted-foreground mt-0.5">{title}</p>
  </div>
);

const YEARLY_PRICES = {
  'trial': 0,
  'temel': 6900,
  'profesyonel': 10680,
  'kurumsal': 15900,
};

export default function AdminCompaniesPage() {
  const { t } = useI18n();
  const { companies, packages, isLoading, fetchCompanies, fetchPackages, suspendCompany, activateCompany, updateCompanyLimits, assignPackage, deleteCompany, approvePayment, impersonateCompany } = useAdminStore();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [packageFilter, setPackageFilter] = useState('all');
  const [waFilter, setWaFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [limitsDialog, setLimitsDialog] = useState(null);
  const [limitsForm, setLimitsForm] = useState({});
  const [subDialog, setSubDialog] = useState(null);
  const [subForm, setSubForm] = useState({ package_id: '', months: 12, billing_type: 'yearly', notes: '' });
  const [detailDialog, setDetailDialog] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchCompanies();
    fetchPackages();
  }, [fetchCompanies, fetchPackages]);

  const filtered = useMemo(() => {
    let list = companies.filter((c) => {
      const matchSearch = !search ||
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.email.toLowerCase().includes(search.toLowerCase()) ||
        (c.tax_number || '').includes(search);
      const matchStatus = statusFilter === 'all' ||
        (statusFilter === 'suspended' ? c.is_suspended : c.subscription_status === statusFilter);
      const matchPackage = packageFilter === 'all' ||
        (c.package_slug === packageFilter) ||
        (packageFilter === 'none' && !c.package_slug);
      const matchWa = waFilter === 'all' ||
        (waFilter === 'connected' ? c.whatsapp_connected : !c.whatsapp_connected);
      return matchSearch && matchStatus && matchPackage && matchWa;
    });

    list.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name': aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase(); break;
        case 'user_count': aVal = a.user_count; bVal = b.user_count; break;
        case 'payment_count': aVal = a.payment_count; bVal = b.payment_count; break;
        case 'message_count': aVal = a.message_count; bVal = b.message_count; break;
        case 'cari_count': aVal = a.cari_count; bVal = b.cari_count; break;
        case 'subscription_price': aVal = a.subscription_price; bVal = b.subscription_price; break;
        default: aVal = a.created_at || ''; bVal = b.created_at || '';
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [companies, search, statusFilter, packageFilter, waFilter, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />;
    return sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />;
  };

  const handleSuspend = async (id) => {
    const res = await suspendCompany(id, 'Admin tarafindan askiya alindi');
    if (res.success) toast.success('Firma askiya alindi');
    else toast.error(res.error);
  };

  const handleActivate = async (id) => {
    const res = await activateCompany(id);
    if (res.success) toast.success('Firma aktif edildi');
    else toast.error(res.error);
  };

  const handleDeleteCompany = async (company) => {
    if (!window.confirm(`"${company.name}" firmasini ve TUM verilerini silmek istediginize emin misiniz? Bu islem geri alinamaz!`)) return;
    const res = await deleteCompany(company.id);
    if (res.success) toast.success('Firma silindi');
    else toast.error(res.error);
  };

  const handleApprovePayment = async (company) => {
    if (!window.confirm(`"${company.name}" firmasının havale ödemesini onaylamak ve aboneliğini aktif etmek istediğinize emin misiniz?`)) return;
    const res = await approvePayment(company.id);
    if (res.success) {
      toast.success(res.data?.message || 'Ödeme onaylandı');
      fetchCompanies();
    } else toast.error(res.error || 'Onay başarısız');
  };

  const handleImpersonate = async (company) => {
    const res = await impersonateCompany(company.id);
    if (res.success) {
      const { access_token, user } = res.data;
      // Save current admin session for returning
      const currentAuth = useAuthStore.getState();
      localStorage.setItem('admin_backup_token', currentAuth.token);
      localStorage.setItem('admin_backup_user', JSON.stringify(currentAuth.user));
      // Set new session
      useAuthStore.setState({
        token: access_token,
        user: user,
        isAuthenticated: true,
        isSuspended: false,
        isTrialExpired: false,
      });
      toast.success(`"${company.name}" hesabına giriş yapıldı`);
      navigate('/dashboard');
    } else {
      toast.error(res.error || 'Giriş yapılamadı');
    }
  };

  const openLimits = (company) => {
    setLimitsForm(company.limits || {});
    setLimitsDialog(company);
  };

  const saveLimits = async () => {
    const res = await updateCompanyLimits(limitsDialog.id, limitsForm);
    if (res.success) {
      toast.success('Limitler guncellendi');
      setLimitsDialog(null);
    } else toast.error(res.error);
  };

  const openSubDialog = (company) => {
    setSubForm({
      package_id: company.package_id || '',
      months: 12,
      billing_type: 'yearly',
      notes: company.subscription_notes || '',
    });
    setSubDialog(company);
  };

  const selectedPackage = packages.find((p) => p.id === subForm.package_id);
  const yearlyPrice = selectedPackage ? (YEARLY_PRICES[selectedPackage.slug] || (selectedPackage.monthly_price * 12)) : 0;

  const handleAssignPackage = async () => {
    if (!subForm.package_id) {
      toast.error('Lutfen bir paket secin');
      return;
    }
    const res = await assignPackage(subDialog.id, subForm);
    if (res.success) {
      toast.success(res.data?.message || 'Paket atandi');
      setSubDialog(null);
      fetchCompanies();
    } else toast.error(res.error);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('tr-TR'); } catch { return '-'; }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setPackageFilter('all');
    setWaFilter('all');
  };

  const hasActiveFilters = search || statusFilter !== 'all' || packageFilter !== 'all' || waFilter !== 'all';

  const totalCount = companies.length;
  const activeCount = companies.filter(c => c.subscription_status === 'active' && !c.is_suspended).length;
  const trialCount = companies.filter(c => c.subscription_status === 'trial' && !c.is_suspended).length;
  const pendingCount = companies.filter(c => c.subscription_status === 'pending_payment').length;
  const expiredCount = companies.filter(c => c.subscription_status === 'expired').length;
  const suspendedCount = companies.filter(c => c.is_suspended).length;

  if (isLoading && companies.length === 0) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="admin-companies-page">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">Firma Yonetimi</h1>
        <div className="space-y-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in" data-testid="admin-companies-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">Firma Yonetimi</h1>
        <p className="mt-1 text-sm text-muted-foreground">{companies.length} firma kayitli</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3" data-testid="company-summary-cards">
        <SummaryCard title="Toplam" value={totalCount} color="bg-slate-50 border-slate-200" />
        <SummaryCard title="Aktif" value={activeCount} color="bg-green-50 border-green-200 text-green-700" />
        <SummaryCard title="Deneme" value={trialCount} color="bg-amber-50 border-amber-200 text-amber-700" />
        <SummaryCard title="Havale Bekliyor" value={pendingCount} color="bg-orange-50 border-orange-200 text-orange-700" />
        <SummaryCard title="Suresi Dolmus" value={expiredCount} color="bg-red-50 border-red-200 text-red-700" />
        <SummaryCard title="Askida" value={suspendedCount} color="bg-red-50 border-red-300 text-red-800" />
      </div>

      {/* Search & Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Firma, e-posta veya vergi no ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="company-search"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={showFilters ? "default" : "outline"}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters-btn"
              >
                <Filter className="h-4 w-4 mr-1" />
                Filtre
                {hasActiveFilters && <Badge className="ml-1 h-4 w-4 p-0 text-[9px] rounded-full" variant="destructive">!</Badge>}
              </Button>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="h-4 w-4 mr-1" />Temizle
                </Button>
              )}
            </div>
          </div>
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 pt-3 border-t" data-testid="filter-panel">
              <div>
                <Label className="text-xs mb-1 block">{t('status')}</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tumu</SelectItem>
                    <SelectItem value="active">{t('active')}</SelectItem>
                    <SelectItem value="trial">Deneme</SelectItem>
                    <SelectItem value="pending_payment">Havale Onayı Bekliyor</SelectItem>
                    <SelectItem value="expired">Suresi Dolmus</SelectItem>
                    <SelectItem value="suspended">Askida</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Paket</Label>
                <Select value={packageFilter} onValueChange={setPackageFilter}>
                  <SelectTrigger data-testid="package-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tumu</SelectItem>
                    <SelectItem value="none">Paket Yok</SelectItem>
                    {packages.map(p => <SelectItem key={p.id} value={p.slug}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">WhatsApp</Label>
                <Select value={waFilter} onValueChange={setWaFilter}>
                  <SelectTrigger data-testid="wa-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tumu</SelectItem>
                    <SelectItem value="connected">Bagli</SelectItem>
                    <SelectItem value="disconnected">Bagli Degil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Companies - Desktop Table */}
      <Card className="border-0 shadow-sm overflow-hidden hidden lg:block">
        <div className="overflow-x-auto">
          <Table data-testid="companies-table">
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="min-w-[200px]">
                  <button onClick={() => toggleSort('name')} className="flex items-center gap-1 text-xs font-semibold hover:text-primary transition-colors">
                    Firma <SortIcon field="name" />
                  </button>
                </TableHead>
                <TableHead className="text-center min-w-[80px]">{t('status')}</TableHead>
                <TableHead className="min-w-[100px]">Paket</TableHead>
                <TableHead className="text-center min-w-[80px]">Ödeme</TableHead>
                <TableHead className="text-center">
                  <button onClick={() => toggleSort('user_count')} className="flex items-center gap-1 text-xs font-semibold hover:text-primary transition-colors mx-auto">
                    <Users className="h-3 w-3" /> <SortIcon field="user_count" />
                  </button>
                </TableHead>
                <TableHead className="text-center">
                  <button onClick={() => toggleSort('cari_count')} className="flex items-center gap-1 text-xs font-semibold hover:text-primary transition-colors mx-auto">
                    <FileText className="h-3 w-3" /> <SortIcon field="cari_count" />
                  </button>
                </TableHead>
                <TableHead className="text-center">
                  <button onClick={() => toggleSort('payment_count')} className="flex items-center gap-1 text-xs font-semibold hover:text-primary transition-colors mx-auto">
                    <CreditCard className="h-3 w-3" /> <SortIcon field="payment_count" />
                  </button>
                </TableHead>
                <TableHead className="text-center">
                  <button onClick={() => toggleSort('message_count')} className="flex items-center gap-1 text-xs font-semibold hover:text-primary transition-colors mx-auto">
                    <MessageCircle className="h-3 w-3" /> <SortIcon field="message_count" />
                  </button>
                </TableHead>
                <TableHead className="text-center">WA</TableHead>
                <TableHead>
                  <button onClick={() => toggleSort('created_at')} className="flex items-center gap-1 text-xs font-semibold hover:text-primary transition-colors">
                    Kayit <SortIcon field="created_at" />
                  </button>
                </TableHead>
                <TableHead className="text-right min-w-[160px]">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow key={c.id} className="group hover:bg-muted/20 transition-colors" data-testid={`company-row-${c.id}`}>
                  <TableCell>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate max-w-[200px]">{c.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate max-w-[200px]">{c.email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {c.is_suspended ? (
                      <Badge variant="destructive" className="text-[10px]">Askida</Badge>
                    ) : (
                      <SubscriptionBadge status={c.subscription_status || 'trial'} />
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-xs">
                      {c.package_name ? (
                        <Badge variant="secondary" className="text-[10px]">{c.package_name}</Badge>
                      ) : c.registered_package_name && c.registered_package_name !== 'Ucretsiz Deneme' ? (
                        <Badge variant="outline" className="text-[10px] text-muted-foreground">{c.registered_package_name}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {c.selected_payment_method_type === 'credit_card' ? (
                      <Badge className="text-[9px] bg-blue-100 text-blue-700 border-blue-200">
                        <CreditCard className="h-3 w-3 mr-0.5" />KK
                      </Badge>
                    ) : c.selected_payment_method_type === 'bank_transfer' ? (
                      <Badge className="text-[9px] bg-green-100 text-green-700 border-green-200">
                        Havale
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-[10px]">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center text-sm">{c.user_count}</TableCell>
                  <TableCell className="text-center text-sm">{c.cari_count}</TableCell>
                  <TableCell className="text-center text-sm">{c.payment_count}</TableCell>
                  <TableCell className="text-center text-sm">{c.message_count}</TableCell>
                  <TableCell className="text-center">
                    {c.whatsapp_connected ? (
                      <Wifi className="h-4 w-4 text-green-500 mx-auto" />
                    ) : (
                      <WifiOff className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(c.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDetailDialog(c)} title="Detay" data-testid={`detail-btn-${c.id}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => handleImpersonate(c)} title="Hesaba Gir" data-testid={`impersonate-btn-${c.id}`}>
                        <LogIn className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openLimits(c)} title="Limitler" data-testid={`limits-btn-${c.id}`}>
                        <Settings2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openSubDialog(c)} title="Paket & Abonelik" data-testid={`sub-btn-${c.id}`}>
                        <Crown className="h-3.5 w-3.5" />
                      </Button>
                      {c.subscription_status === 'pending_payment' && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-orange-600 hover:text-orange-700 hover:bg-orange-50" onClick={() => handleApprovePayment(c)} title="Havale Onayla" data-testid={`approve-btn-${c.id}`}>
                          <CheckCircle className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {c.is_suspended ? (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600" onClick={() => handleActivate(c.id)} title="Aktif Et" data-testid={`activate-btn-${c.id}`}>
                          <CheckCircle className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-orange-600" onClick={() => handleSuspend(c.id)} title="Askiya Al" data-testid={`suspend-btn-${c.id}`}>
                          <Ban className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteCompany(c)} title="Sil" data-testid={`delete-company-${c.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="py-12 text-center text-muted-foreground">
                    Firma bulunamadi
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="px-4 py-2 border-t bg-muted/20 text-xs text-muted-foreground">
          {filtered.length} / {companies.length} firma gosteriliyor
        </div>
      </Card>

      {/* Companies - Mobile Cards */}
      <div className="lg:hidden space-y-3" data-testid="companies-mobile">
        {filtered.map((c) => (
          <Card key={c.id} className="overflow-hidden" data-testid={`company-card-${c.id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.email}</p>
                  {c.phone && <p className="text-xs text-muted-foreground">{c.phone}</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {c.is_suspended ? (
                    <Badge variant="destructive" className="text-[10px]">Askida</Badge>
                  ) : (
                    <SubscriptionBadge status={c.subscription_status || 'trial'} />
                  )}
                  {c.whatsapp_connected ? <Wifi className="h-4 w-4 text-green-500" /> : <WifiOff className="h-4 w-4 text-muted-foreground/40" />}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {c.package_name && <Badge variant="secondary" className="text-[10px]">{c.package_name}</Badge>}
                {c.selected_payment_method_type === 'credit_card' && <Badge className="text-[9px] bg-blue-100 text-blue-700 border-blue-200"><CreditCard className="h-3 w-3 mr-0.5" />KK</Badge>}
                {c.selected_payment_method_type === 'bank_transfer' && <Badge className="text-[9px] bg-green-100 text-green-700 border-green-200">Havale</Badge>}
              </div>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="rounded-md bg-muted/50 p-1.5">
                  <Users className="h-3 w-3 mx-auto text-muted-foreground mb-0.5" />
                  <p className="text-xs font-semibold">{c.user_count}</p>
                </div>
                <div className="rounded-md bg-muted/50 p-1.5">
                  <FileText className="h-3 w-3 mx-auto text-muted-foreground mb-0.5" />
                  <p className="text-xs font-semibold">{c.cari_count}</p>
                </div>
                <div className="rounded-md bg-muted/50 p-1.5">
                  <CreditCard className="h-3 w-3 mx-auto text-muted-foreground mb-0.5" />
                  <p className="text-xs font-semibold">{c.payment_count}</p>
                </div>
                <div className="rounded-md bg-muted/50 p-1.5">
                  <MessageCircle className="h-3 w-3 mx-auto text-muted-foreground mb-0.5" />
                  <p className="text-xs font-semibold">{c.message_count}</p>
                </div>
              </div>
              <div className="flex items-center justify-between border-t pt-2">
                <span className="text-[10px] text-muted-foreground">{formatDate(c.created_at)}</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDetailDialog(c)}><Eye className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600" onClick={() => handleImpersonate(c)}><LogIn className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openSubDialog(c)}><Crown className="h-4 w-4" /></Button>
                  {c.subscription_status === 'pending_payment' && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-orange-600" onClick={() => handleApprovePayment(c)}><CheckCircle className="h-4 w-4" /></Button>
                  )}
                  {c.is_suspended ? (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600" onClick={() => handleActivate(c.id)}><CheckCircle className="h-4 w-4" /></Button>
                  ) : (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-orange-600" onClick={() => handleSuspend(c.id)}><Ban className="h-4 w-4" /></Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div className="py-12 text-center text-muted-foreground">Firma bulunamadi</div>
        )}
        <p className="text-xs text-muted-foreground text-center py-2">{filtered.length} / {companies.length} firma</p>
      </div>

      {/* Company Detail / Billing Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="company-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              {detailDialog?.name}
            </DialogTitle>
            <DialogDescription>Firma detay ve fatura bilgileri</DialogDescription>
          </DialogHeader>
          {detailDialog && (
            <div className="space-y-4 text-sm">
              {/* Contact Info */}
              <div className="space-y-2">
                <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">Iletisim Bilgileri</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">E-posta:</span></div>
                  <div className="font-medium break-all">{detailDialog.email}</div>
                  <div><span className="text-muted-foreground">Telefon:</span></div>
                  <div className="font-medium">{detailDialog.phone || '-'}</div>
                  <div><span className="text-muted-foreground">Yetkili:</span></div>
                  <div className="font-medium">{detailDialog.contact_name || '-'}</div>
                  <div><span className="text-muted-foreground">Yetkili Tel:</span></div>
                  <div className="font-medium">{detailDialog.contact_phone || '-'}</div>
                </div>
              </div>

              <Separator />

              {/* Billing Info */}
              <div className="space-y-2">
                <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">Fatura Bilgileri</h4>
                <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="text-muted-foreground">Vergi No:</span></div>
                    <div className="font-medium font-mono">{detailDialog.tax_number || '-'}</div>
                    <div><span className="text-muted-foreground">Vergi Dairesi:</span></div>
                    <div className="font-medium">{detailDialog.tax_office || '-'}</div>
                    <div><span className="text-muted-foreground">Adres:</span></div>
                    <div className="font-medium col-span-1">{detailDialog.address || '-'}</div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Subscription Info */}
              <div className="space-y-2">
                <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">{t('subscriptionInfo')}</h4>                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-muted-foreground">Durum:</span></div>
                  <div>
                    {detailDialog.is_suspended ? (
                      <Badge variant="destructive" className="text-[10px]">Askida</Badge>
                    ) : (
                      <SubscriptionBadge status={detailDialog.subscription_status} />
                    )}
                  </div>
                  <div><span className="text-muted-foreground">Paket:</span></div>
                  <div className="font-medium">{detailDialog.package_name || 'Yok'}</div>
                  {detailDialog.registered_package_name && (
                    <>
                      <div><span className="text-muted-foreground">Kayıt Paketi:</span></div>
                      <div className="font-medium text-primary">{detailDialog.registered_package_name}</div>
                    </>
                  )}
                  <div><span className="text-muted-foreground">Başlangıç:</span></div>
                  <div className="font-medium">{formatDate(detailDialog.subscription_start) || formatDate(detailDialog.trial_start)}</div>
                  <div><span className="text-muted-foreground">Bitiş:</span></div>
                  <div className="font-medium">{formatDate(detailDialog.subscription_end) || formatDate(detailDialog.trial_end)}</div>
                  {detailDialog.subscription_price > 0 && (
                    <>
                      <div><span className="text-muted-foreground">Ücret:</span></div>
                      <div className="font-medium">{detailDialog.subscription_price.toLocaleString('tr-TR')} TL{detailDialog.billing_type === 'yearly' ? '/yıl' : '/ay'}</div>
                    </>
                  )}
                  <div><span className="text-muted-foreground">Ödeme Yöntemi:</span></div>
                  <div>
                    {detailDialog.selected_payment_method_type === 'credit_card' ? (
                      <Badge className="text-[10px] bg-blue-100 text-blue-700 border-blue-200">
                        <CreditCard className="h-3 w-3 mr-1" />Kredi Kartı (PayTR)
                      </Badge>
                    ) : detailDialog.selected_payment_method_type === 'bank_transfer' ? (
                      <Badge className="text-[10px] bg-green-100 text-green-700 border-green-200">
                        <Building2 className="h-3 w-3 mr-1" />Havale / EFT
                      </Badge>
                    ) : detailDialog.subscription_status === 'trial' ? (
                      <span className="text-xs text-muted-foreground">Deneme</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Usage Stats */}
              <div className="space-y-2">
                <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">Kullanım İstatistikleri</h4>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="rounded-lg border p-2">
                    <p className="text-lg font-bold">{detailDialog.user_count}</p>
                    <p className="text-[10px] text-muted-foreground">Kullanıcı</p>
                  </div>
                  <div className="rounded-lg border p-2">
                    <p className="text-lg font-bold">{detailDialog.cari_count}</p>
                    <p className="text-[10px] text-muted-foreground">Cari</p>
                  </div>
                  <div className="rounded-lg border p-2">
                    <p className="text-lg font-bold">{detailDialog.payment_count}</p>
                    <p className="text-[10px] text-muted-foreground">Odeme</p>
                  </div>
                  <div className="rounded-lg border p-2">
                    <p className="text-lg font-bold">{detailDialog.message_count}</p>
                    <p className="text-[10px] text-muted-foreground">{t('messageText')}</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="default" onClick={() => { handleImpersonate(detailDialog); setDetailDialog(null); }} data-testid="impersonate-from-detail">
                  <LogIn className="mr-1 h-4 w-4" />Hesaba Gir
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setDetailDialog(null); openLimits(detailDialog); }}>
                  <Settings2 className="mr-1 h-4 w-4" />Limitler
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setDetailDialog(null); openSubDialog(detailDialog); }}>
                  <Crown className="mr-1 h-4 w-4" />Paket Ata
                </Button>
                {detailDialog.subscription_status === 'pending_payment' && (
                  <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={() => { handleApprovePayment(detailDialog); setDetailDialog(null); }}>
                    <CheckCircle className="mr-1 h-4 w-4" />Havale Onayla
                  </Button>
                )}
                {detailDialog.is_suspended ? (
                  <Button size="sm" onClick={() => { handleActivate(detailDialog.id); setDetailDialog(null); }}>
                    <CheckCircle className="mr-1 h-4 w-4" />Aktif Et
                  </Button>
                ) : (
                  <Button variant="destructive" size="sm" onClick={() => { handleSuspend(detailDialog.id); setDetailDialog(null); }}>
                    <Ban className="mr-1 h-4 w-4" />Askiya Al
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Limits Dialog */}
      <Dialog open={!!limitsDialog} onOpenChange={() => setLimitsDialog(null)}>
        <DialogContent data-testid="limits-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Firma Limitleri - {limitsDialog?.name}</DialogTitle>
            <DialogDescription>Firma kaynak limitlerini duzenleyin</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {[
              { key: 'daily_message_limit', label: 'Gunluk Mesaj Limiti' },
              { key: 'monthly_message_limit', label: 'Aylik Mesaj Limiti' },
              { key: 'personnel_limit', label: 'Personel Limiti' },
              { key: 'cari_limit', label: 'Cari Limiti' },
              { key: 'payment_limit', label: 'Odeme Limiti' },
            ].map(({ key, label }) => (
              <div key={key} className="grid grid-cols-2 items-center gap-4">
                <Label>{label}</Label>
                <Input
                  type="number"
                  value={limitsForm[key] || 0}
                  onChange={(e) => setLimitsForm({ ...limitsForm, [key]: parseInt(e.target.value) || 0 })}
                  data-testid={`limit-input-${key}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLimitsDialog(null)}>Iptal</Button>
            <Button onClick={saveLimits} data-testid="save-limits-btn">{t('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Package & Subscription Dialog */}
      <Dialog open={!!subDialog} onOpenChange={() => setSubDialog(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="subscription-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-primary" />
              Paket & Abonelik - {subDialog?.name}
            </DialogTitle>
            <DialogDescription>Firmaya abonelik paketi atayin</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 overflow-y-auto flex-1 min-h-0">
            {/* Current Status */}
            <div className="rounded-lg bg-muted/50 p-3 text-sm space-y-1">
              <p><span className="font-medium">Durum:</span> {subDialog?.subscription_status === 'active' ? 'Aktif' : subDialog?.subscription_status === 'expired' ? 'Suresi Dolmus' : 'Deneme'}</p>
              {subDialog?.package_name && <p><span className="font-medium">Mevcut Paket:</span> {subDialog.package_name}</p>}
              {subDialog?.registered_package_name && subDialog?.registered_package_name !== 'Ucretsiz Deneme' && (
                <p><span className="font-medium">Kayit Sirasinda Secilen:</span> <span className="text-primary font-medium">{subDialog.registered_package_name}</span></p>
              )}
              {subDialog?.subscription_end && <p><span className="font-medium">Bitiş:</span> {formatDate(subDialog.subscription_end)}</p>}
            </div>

            <Separator />

            {/* Package Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Paket Secin</Label>
              <div className="grid gap-2 grid-cols-2" data-testid="package-selection-grid">
                {packages.filter(p => p.slug !== 'trial').map((pkg) => (
                  <button
                    key={pkg.id}
                    type="button"
                    onClick={() => setSubForm({ ...subForm, package_id: pkg.id })}
                    className={`rounded-lg border p-3 text-left transition-all ${subForm.package_id === pkg.id ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:border-primary/50'}`}
                    data-testid={`select-package-${pkg.slug}`}
                  >
                    <p className="font-medium text-sm">{pkg.name}</p>
                    <p className="text-primary font-bold text-base mt-0.5">{(YEARLY_PRICES[pkg.slug] || (pkg.monthly_price * 12)).toLocaleString('tr-TR')} TL/yil</p>
                    <p className="text-[10px] text-muted-foreground">Aylik {pkg.monthly_price.toLocaleString('tr-TR')} TL</p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {pkg.is_unlimited_personnel ? 'Sinirsiz' : pkg.personnel_limit} Calisan | {pkg.is_unlimited_messages ? 'Sinirsiz' : pkg.monthly_message_limit} Mesaj
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Billing Type */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Odeme Tipi</Label>
              <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
                <span className="font-medium">Yillik Abonelik</span>
                <span className="text-xs text-muted-foreground ml-2">(Tum paketler yillik faturalandirilir)</span>
              </div>
            </div>

            {/* Duration */}
            <div className="space-y-1.5">
              <Label>Sure (Ay)</Label>
              <Input
                type="number"
                min={1}
                max={36}
                value={subForm.months}
                onChange={(e) => setSubForm({ ...subForm, months: parseInt(e.target.value) || 1 })}
                data-testid="sub-months-input"
              />
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label>{t('customerNotes')}</Label>
              <Input value={subForm.notes} onChange={(e) => setSubForm({ ...subForm, notes: e.target.value })} placeholder="Abonelik notu..." data-testid="sub-notes-input" />
            </div>

            {/* Price Summary */}
            {selectedPackage && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm" data-testid="price-summary">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Paket:</span>
                  <span className="font-medium">{selectedPackage.name}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-muted-foreground">Yillik Ucret:</span>
                  <span className="font-medium">{yearlyPrice.toLocaleString('tr-TR')} TL</span>
                </div>
                <div className="flex justify-between mt-1 text-muted-foreground">
                  <span>Aylik:</span>
                  <span>{selectedPackage.monthly_price.toLocaleString('tr-TR')} TL</span>
                </div>
                <Separator className="my-2" />
                <div className="flex justify-between font-bold text-base">
                  <span>Toplam ({subForm.months} ay):</span>
                  <span className="text-primary">{(selectedPackage.monthly_price * subForm.months).toLocaleString('tr-TR')} TL</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="shrink-0 border-t pt-3">
            <Button variant="outline" onClick={() => setSubDialog(null)}>Iptal</Button>
            <Button onClick={handleAssignPackage} disabled={!subForm.package_id} data-testid="assign-package-btn">
              <Package className="mr-1 h-4 w-4" />Paket Ata
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
